import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';

export interface OrganizationBranding {
    id?: number;
    organizationName: string;
    logoPath?: string;
    logoMime?: string;
    faviconPath?: string;
    primaryColor: string;
    secondaryColor: string;
    sidebarBgColor: string;
    sidebarTextColor: string;
    updatedAt?: Date;
}

export interface UpdateBrandingRequest {
    organizationName?: string;
    primaryColor?: string;
    secondaryColor?: string;
    sidebarBgColor?: string;
    sidebarTextColor?: string;
}

@Injectable({
    providedIn: 'root'
})
export class BrandingService {
    private apiUrl = environment.apiUrl;

    private brandingSubject = new BehaviorSubject<OrganizationBranding>({
        organizationName: 'Document Management System',
        primaryColor: '#9c4337',
        secondaryColor: '#424242',
        sidebarBgColor: '#1e2a3a',
        sidebarTextColor: '#ffffff'
    });
    public branding$ = this.brandingSubject.asObservable();

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) {
        // Load branding on service initialization
        this.loadBranding();
    }

    private getAuthHeaders(): HttpHeaders {
        const token = this.authService.getToken();
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`
        });
    }

    loadBranding(): void {
        this.http.get<OrganizationBranding>(`${this.apiUrl}/branding`).subscribe(
            branding => {
                this.brandingSubject.next(branding);
                this.applyBrandingStyles(branding);
            },
            error => console.error('Failed to load branding:', error)
        );
    }

    getBranding(): Observable<OrganizationBranding> {
        return this.http.get<OrganizationBranding>(`${this.apiUrl}/branding`).pipe(
            tap(branding => {
                this.brandingSubject.next(branding);
                this.applyBrandingStyles(branding);
            }),
            catchError(error => {
                console.error('Get branding error:', error);
                return throwError(error.error?.error || 'Failed to load branding');
            })
        );
    }

    updateBranding(data: UpdateBrandingRequest): Observable<OrganizationBranding> {
        return this.http.put<OrganizationBranding>(`${this.apiUrl}/branding`, data, {
            headers: this.getAuthHeaders()
        }).pipe(
            tap(branding => {
                this.brandingSubject.next(branding);
                this.applyBrandingStyles(branding);
            }),
            catchError(error => {
                console.error('Update branding error:', error);
                return throwError(error.error?.error || 'Failed to update branding');
            })
        );
    }

    uploadLogo(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('logo', file);

        return this.http.post<any>(`${this.apiUrl}/branding/logo`, formData, {
            headers: new HttpHeaders({
                'Authorization': `Bearer ${this.authService.getToken()}`
            })
        }).pipe(
            tap(response => {
                const currentBranding = this.brandingSubject.value;
                this.brandingSubject.next({
                    ...currentBranding,
                    logoPath: response.logoPath,
                    logoMime: response.logoMime
                });
            }),
            catchError(error => {
                console.error('Upload logo error:', error);
                return throwError(error.error?.error || 'Failed to upload logo');
            })
        );
    }

    deleteLogo(): Observable<any> {
        return this.http.delete<any>(`${this.apiUrl}/branding/logo`, {
            headers: this.getAuthHeaders()
        }).pipe(
            tap(() => {
                const currentBranding = this.brandingSubject.value;
                this.brandingSubject.next({
                    ...currentBranding,
                    logoPath: undefined,
                    logoMime: undefined
                });
            }),
            catchError(error => {
                console.error('Delete logo error:', error);
                return throwError(error.error?.error || 'Failed to delete logo');
            })
        );
    }

    getLogoUrl(): string {
        const branding = this.brandingSubject.value;
        if (branding?.logoPath) {
            return `${this.apiUrl}/branding/logo?t=${Date.now()}`;
        }
        return 'assets/images/logo-icon.png';
    }

    getLogoBlobUrl(): Observable<string> {
        return this.http.get(`${this.apiUrl}/branding/logo`, {
            headers: this.getAuthHeaders(),
            responseType: 'blob'
        }).pipe(
            map(blob => URL.createObjectURL(blob)),
            catchError(error => {
                console.error('Failed to load logo:', error);
                return throwError(error);
            })
        );
    }

    getCurrentBranding(): OrganizationBranding {
        return this.brandingSubject.value;
    }

    private applyBrandingStyles(branding: OrganizationBranding): void {
        const root = document.documentElement;
        root.style.setProperty('--primary-color', branding.primaryColor);
        root.style.setProperty('--secondary-color', branding.secondaryColor);
        root.style.setProperty('--sidebar-bg-color', branding.sidebarBgColor);
        root.style.setProperty('--sidebar-text-color', branding.sidebarTextColor);

        // Update page title
        if (branding.organizationName) {
            document.title = branding.organizationName;
        }
    }
}
